/**
 * Brera Muse · Ön bilgilendirme ve mesafeli satış sözleşmesi onayı
 *
 * snippets/bm-legal-consent.liquid içindeki kutunun davranışı. snippets/scripts.liquid
 * üzerinden her sayfada yüklenir; böylece sepet çekmecesi sonradan yenilense de çalışır.
 *
 * - Kutu işaretli değilken ödeme butonu gerçekten devre dışı bırakılır (disabled).
 *   Tema butonu yeniden çizerse MutationObserver kilidi tekrar uygular.
 * - Onay; tarih, saat ve metin sürümüyle sepet özniteliği olarak kaydedilir.
 * - Sepetteki ürünler değişirse onay sıfırlanır.
 */
import { StandardEvents } from '@shopify/events';

const state = { ready: false, checked: false, value: '', key: 'Ön bilgilendirme onayı', version: '' };
let saving = Promise.resolve();
let syncQueued = false;

const elements = () => [...document.querySelectorAll('bm-legal-consent')];

function stamp() {
  const time = new Intl.DateTimeFormat('tr-TR', {
    timeZone: 'Europe/Istanbul',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).format(new Date());
  return `Onaylandı – ${time} (TR saati) – Metin sürümü: ${state.version}`;
}

function paint(el) {
  const box = el.querySelector('.bm-consent__input');
  const field = el.querySelector('.bm-consent__field');
  if (box && box.checked !== state.checked) box.checked = state.checked;
  if (field && field.value !== state.value) field.value = state.value;
  if (state.checked) el.classList.remove('is-error');
}

/** Ödeme butonlarını onay durumuna göre kilitler / açar. */
function syncButtons() {
  syncQueued = false;
  for (const button of document.querySelectorAll('button[name="checkout"]')) {
    const scope = button.closest('.cart__ctas');
    const guarded = Boolean(scope && scope.querySelector('bm-legal-consent'));
    const locked = button.hasAttribute('data-bm-consent-locked');

    if (guarded && !state.checked) {
      // Tema butonu zaten kapatmışsa (ör. boş sepet) kilidi biz koymayız, ona dokunmayız.
      if (!button.disabled) {
        button.disabled = true;
        button.setAttribute('data-bm-consent-locked', '');
      }
    } else if (locked) {
      button.removeAttribute('data-bm-consent-locked');
      button.disabled = false;
    }
  }
}

function queueSync() {
  if (syncQueued) return;
  syncQueued = true;
  requestAnimationFrame(syncButtons);
}

function setState(checked) {
  state.checked = checked;
  state.value = checked ? stamp() : '';
  elements().forEach(paint);
  syncButtons();

  const root = window.Shopify?.routes?.root || '/';
  const body = JSON.stringify({ attributes: { [state.key]: state.value } });
  saving = saving
    .catch(() => {})
    .then(() =>
      fetch(`${root}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'same-origin',
        body,
      })
    )
    .catch((error) => console.warn('[bm-consent] onay kaydedilemedi', error));
}

function showError() {
  const visible = elements().filter((el) => el.offsetParent !== null);
  (visible.length ? visible : elements()).forEach((el) => {
    el.classList.remove('is-error');
    void el.offsetWidth;
    el.classList.add('is-error');
  });
  const first = visible[0];
  if (first) {
    first.scrollIntoView({ block: 'center', behavior: 'smooth' });
    first.querySelector('.bm-consent__input')?.focus({ preventScroll: true });
  }
}

const blocked = () => elements().length > 0 && !state.checked;

class BmLegalConsent extends HTMLElement {
  connectedCallback() {
    const box = this.querySelector('.bm-consent__input');
    const field = this.querySelector('.bm-consent__field');
    if (!state.ready) {
      state.ready = true;
      state.key = this.dataset.key || state.key;
      state.version = this.dataset.version || '';
      state.value = field?.value || '';
      state.checked = Boolean(box?.checked && state.value);
    }
    paint(this);
    queueSync();
  }
}

if (!customElements.get('bm-legal-consent')) {
  customElements.define('bm-legal-consent', BmLegalConsent);

  document.addEventListener('change', (event) => {
    const box = event.target.closest?.('.bm-consent__input');
    if (box) setState(box.checked);
  });

  // Yedek kilit: buton bir şekilde açık kalırsa tıklama ve gönderim yine durdurulur.
  window.addEventListener(
    'click',
    (event) => {
      const button = event.target.closest?.('button[name="checkout"], input[name="checkout"], a[href$="/checkout"]');
      if (!button || !blocked()) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      showError();
    },
    true
  );

  window.addEventListener(
    'submit',
    (event) => {
      const submitter = event.submitter;
      if (!submitter || submitter.name !== 'checkout') return;
      if (blocked()) {
        event.preventDefault();
        event.stopImmediatePropagation();
        showError();
        return;
      }
      document.querySelectorAll('.bm-consent__field').forEach((field) => (field.value = state.value));
    },
    true
  );

  // Tarayıcının kendi "kutuyu işaretleyin" uyarısı yerine bizim uyarımız
  document.addEventListener(
    'invalid',
    (event) => {
      if (!event.target.closest?.('.bm-consent__input')) return;
      event.preventDefault();
      showError();
    },
    true
  );

  // Sepet değişirse onay sıfırlanır: müşteri son sepet için yeniden onaylar.
  if (StandardEvents?.cartLinesUpdate) {
    document.addEventListener(StandardEvents.cartLinesUpdate, () => {
      if (state.checked) setState(false);
    });
  }

  // Tema sepeti yeniden çizdiğinde kilidi tekrar uygula.
  new MutationObserver(queueSync).observe(document.documentElement, {
    subtree: true,
    childList: true,
    attributes: true,
    attributeFilter: ['disabled'],
  });

  queueSync();
}
